import { SingleSpaManager } from "./utils/single-spa-manager";
import { EnvironmentManager } from "./utils/env-manager";
import { NotificationManager } from "./utils/notification-manager";
import {
  UserSessionService,
  UserSessionInfo,
} from "./utils/user-session-service";
import { UserIdleManager } from "./utils/user-idle-manager";
import { pubsub } from "./utils/pubsub";
import { SpinnerManager } from "./utils/spinner-manager";
import "./index.scss";

// Declare global variables for the browser environment
declare var window: any;
declare var sessionStorage: any;

// Key for managing redirect paths in session storage
const SIGNIN_REDIRECTPATH_KEY = "signInRedirect";

let baseIdentityUrl = "";
let authServiceUrl = "";
let logoutUrl = "";
const spinnerManager = new SpinnerManager(pubsub);
/**
 * Entry point of the Single-SPA root application.
 * This function initializes environment variables, validates the user session, and starts Single-SPA.
 */
(async function appStart(): Promise<void> {
  try {
    // Step 1: Initialize environment variables and validate them
    EnvironmentManager.initialize();
    spinnerManager.initialize();

    const userIdleManager = new UserIdleManager({
      idleTimeMs:
        Number(process.env.USER_IDLE_TIMEOUT_MINUTES || 60) * 60 * 1000,
      responseTimeMs:
        Number(process.env.USER_IDLE_RESPONSE_TIMEOUT_MINUTES || 15) *
        60 *
        1000,
      onLogout: handleLogout,
    });

    // Fetch environment variables after validation
    baseIdentityUrl = process.env.BASE_IDENTITY_URL as string;
    authServiceUrl = process.env.AUTH_SERVICE_URL as string;
    const registryUrl = process.env.REGISTRY_PATH as string;

    // Combine base URL and registry path for fetching configuration
    //const registryUrl = `${baseIdentityUrl}${registryPath}`;
    const address = window.location.pathname; // Current browser path
    const params = new URLSearchParams(window.location.search); // Query parameters in the URL

    // Step 2: Handle OIDC (OpenID Connect) callback if the user is returning from login
    if (
      address === "/signin-oidc" &&
      params.has("code") &&
      params.has("state")
    ) {
      await handleOidcCallback(params, baseIdentityUrl);
      return;
    }
    // Step 3: Set redirect path for post-login navigation
    setSignInRedirectPath();

    // Step 4: Validate the user's session
    const userSession = await checkUserSession(baseIdentityUrl, authServiceUrl);
    if (!userSession) {
      handleNavigate(
        `${baseIdentityUrl}/session/login?returnUrl=${encodeURIComponent(
          window.location.href
        )}`
      ); // Redirect to login if the session is invalid
      return;
    }

    logoutUrl = userSession.session.logoutUrl; // Store logout URL for later use

    console.info("[Single-SPA] Started successfully.");
    // addLogoutButton();

    // Step 5: Initialize NotificationManager
    //  const notificationManager = new NotificationManager();
    // notificationManager.initialize(
    //   userSession.impersonatingUser?.id || userSession.currentUser.id, // User ID for notifications
    //   handleLogout, // Logout handler
    //   handleNavigate // Navigate handler
    // );

    // Step 6: Load and initialize Single-SPA
    const singleSpaManager = new SingleSpaManager();
    await singleSpaManager.loadSingleSpa(process.env.REGISTRY_URL as string); // Fetch configuration and register applications
    revealApp();
    userIdleManager.start();
  } catch (error) {
    const msg = error instanceof Error ? error.message : error;

    // Display an error message if the application fails to start
    console.error("", msg);
    document.body.innerHTML = `<h1 style="color: red;">Application Error: ${msg}</h1>`;
    revealApp();
  } finally {
  }
})();

function revealApp(): void {
  document.body.classList.add("app-ready");
  document.body.style.visibility = "visible";
}

/**
 * Check if the user is authenticated by validating their session.
 * @param baseIdentityUrl - Base URL of the identity server.
 * @returns User session details or null if the user is not authenticated.
 */
async function checkUserSession(
  baseIdentityUrl: string,
  authServiceUrl: string
): Promise<UserSessionInfo | null> {
  try {
    const csrfToken = getCsrfTokenFromCookie();
    return await UserSessionService.loadAndStoreUserSession(
      baseIdentityUrl,
      authServiceUrl,
      csrfToken
    );
  } catch (error) {
    console.error("Error fetching user session:", error);
    return null;
  }
}

/**
 * Get user session information from session storage
 * @returns UserSessionInfo or null if not found
 */
function getUserSessionInfo(): UserSessionInfo | null {
  return UserSessionService.getUserSession();
}

/**
 * Clear user session information from session storage
 */
function clearUserSessionInfo(): void {
  UserSessionService.clearUserSession();
}

/**
 * Get current user's permissions from session storage
 * @returns Array of permission strings or null if not found
 */
function getUserPermissions(): string[] | null {
  return UserSessionService.getCurrentUserPermissions();
}

function getCsrfTokenFromCookie(): string | null {
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  return match ? match[1] : null;
}

/**
 * Handle OpenID Connect (OIDC) callback from the identity server.
 * @param params - Query parameters from the URL.
 * @param baseIdentityUrl - Base URL of the identity server.
 */
async function handleOidcCallback(
  params: URLSearchParams,
  baseIdentityUrl: string
): Promise<void> {
  const code = params.get("code"); // Authorization code
  const state = params.get("state"); // State for CSRF protection

  console.info("handleOidcCallback >>> ", code, state);

  // Validate the state parameter
  const storedState = sessionStorage.getItem("oidc_state");
  if (state !== storedState) {
    console.error(
      "State mismatch. Potential Cross-Site Request Forgery (CSRF) threat."
    );
    return;
  }

  const redirectPath = getSignInRedirectPath(); // Get the saved redirect path
  handleNavigate(redirectPath); // Redirect to the saved path
}

/**
 * Set the redirect path for post-login navigation.
 */
function setSignInRedirectPath(): void {
  const currentSignInRedirect = JSON.parse(
    sessionStorage.getItem(SIGNIN_REDIRECTPATH_KEY)
  );

  if (currentSignInRedirect) return;

  const newSignInRedirect =
    window.location.pathname == "/"
      ? process.env.DEFAULT_SIGN_IN_PATH
      : window.location.pathname;

  sessionStorage.setItem(
    SIGNIN_REDIRECTPATH_KEY,
    JSON.stringify(newSignInRedirect)
  );
}

/**
 * Retrieve the redirect path for post-login navigation.
 * @returns The redirect path or a default path if none is saved.
 */
function getSignInRedirectPath(): string {
  const signInRedirect = JSON.parse(
    sessionStorage.getItem(SIGNIN_REDIRECTPATH_KEY)
  );
  if (!signInRedirect || signInRedirect === "/")
    return process.env.DEFAULT_SIGN_IN_PATH as string;
  return signInRedirect;
}

/**
 * Handle navigation to a specific path.
 * @param url - The URL to navigate to.
 */
function handleNavigate(url: string): void {
  console.info(`[agdata-root-config] Navigating to: ${url}`);
  window.location.href = url; // Navigate to the specified URL
}

/**
 * Handle logout functionality.
 */
function handleLogout(): void {
  if (!logoutUrl) {
    console.error("Logout URL is not defined.");
    return;
  }

  console.info("[agdata-root-config] Handling logout event.");
  sessionStorage.clear(); // Clear session storage
  window.location.href = `${baseIdentityUrl}${logoutUrl}&returnUrl=${encodeURIComponent(
    window.location.href
  )}`; // Redirect to the logout URL
}

/**
 * Add a logout button and display session details for the user.
 * @param claims - User claims from the session.
 */
function addLogoutButton(): void {
  const userSessionInfo = getUserSessionInfo();

  const expirationTime = userSessionInfo?.session?.expiresIn
    ? userSessionInfo?.session?.expiresAt?.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Unknown";

  // Build display text
  let displayText = `Logged in as ${userSessionInfo.currentUser.name}`;
  if (userSessionInfo?.impersonatingUser) {
    displayText += ` (Impersonated by: ${
      userSessionInfo.impersonatingUser.name ||
      userSessionInfo.impersonatingUser.email
    })`;
  }
  displayText += ` | Expires: ${expirationTime}`;

  const infoDiv = document.createElement("div");
  infoDiv.textContent = displayText;
  infoDiv.style.cssText = `
    position: fixed;
    bottom: 50px;
    right: 10px;
    padding: 10px;
    background-color: #f1f1f1;
    color: #333;
    border: 1px solid #ccc;
    border-radius: 5px;
    font-family: Arial, sans-serif;
    font-size: 14px;
    max-width: 400px;
  `;

  const button = document.createElement("button");
  button.textContent = "Log Out";
  button.style.cssText = `
    position: fixed;
    bottom: 10px;
    right: 10px;
    padding: 10px 20px;
    background-color: #f44336;
    color: #ffffff;
    border: none;
    border-radius: 5px;
    cursor: pointer;
  `;

  button.addEventListener("click", handleLogout);
  document.body.appendChild(infoDiv);
  document.body.appendChild(button);
}

// Export utility functions and services for use by other modules
export {
  getUserSessionInfo,
  clearUserSessionInfo,
  getUserPermissions,
  UserSessionService,
  pubsub,
};

async function fetchAndDisplayToken(baseIdentityUrl: string) {
  console.log("Requesting token from a MOCKED endpoint...");

  const textAreaElement = document.createElement("textarea");

  // 2. Set the desired attributes (id, rows, cols)
  textAreaElement.id = "comments";
  textAreaElement.rows = 5;
  textAreaElement.cols = 30;
  document.body.appendChild(textAreaElement);

  try {
    const response = await fetch(`${baseIdentityUrl}/token`, {
      method: "GET",
      credentials: "include",
    });

    // Check if the request was successful (status 200-299)
    if (!response.ok) {
      throw new Error(
        `Network response was not ok. Status: ${response.status}`
      );
    }

    const token = await response.text();

    textAreaElement.value = token;
  } catch (error) {
    console.error("Failed to fetch or display token:", error);
    textAreaElement.value =
      "Error: Could not fetch token. See console for details.";
  }
}
